import { Link } from 'react-router-dom';
import { CircleCheck, CircleDashed } from 'lucide-react';
import { BRAND } from '@dru/types';
import { Button, Card, StatusBadge, cn } from '@dru/ui';
import { signoffGap } from '../../../lib/labSignoff';

/**
 * Whether the venue can take bookings, as a short checklist with who fixes
 * each gap.
 *
 * Every line reads a field the venue record or the staff list already
 * carries — nothing here is a new rule. What the venue can fix itself
 * (address, contacts, hours, capacity, staff) says so and offers the button;
 * what the platform holds (empanelment, branches, the test list) says to ask
 * support, because the venue cannot change it from here.
 */

interface StaffLike {
  id: string; status: string; fullName: string; roles: Array<{ key: string }>;
  /** Booleans from the server for people whose name goes on a report; null for others. */
  signingProfile?: { signatureImage: boolean; registration: boolean; registrationExpected: boolean } | null;
}

const ROLE_WORD: Record<string, string> = { PATHOLOGIST: 'Pathologist', LAB_TECHNICIAN: 'Lab technician' };

/**
 * Signers whose profile is incomplete — what is missing for each. Both items
 * are recommended, not required: a report still signs without them, and the
 * person's box then reads "Signed electronically · no signature image on
 * file" with no registration printed. Only the person can add them, from
 * My signature; an administrator sees yes/no and never the image.
 */
export function signingGaps(staff: StaffLike[] | null): Array<{ id: string; name: string; role: string; missing: string[] }> {
  return (staff ?? [])
    .filter((u) => u.status === 'ACTIVE' && u.signingProfile)
    .map((u) => {
      const sp = u.signingProfile!;
      const missing = [
        !sp.signatureImage ? 'signature image' : null,
        sp.registrationExpected && !sp.registration ? 'registration number' : null,
      ].filter(Boolean) as string[];
      const role = u.roles.map((r) => ROLE_WORD[r.key]).find(Boolean) ?? 'Signer';
      return { id: u.id, name: u.fullName, role, missing };
    })
    .filter((g) => g.missing.length > 0);
}

interface Check {
  key: string;
  label: string;
  done: boolean;
  /** Who closes the gap: the venue itself, or the platform. */
  owner: 'venue' | 'platform';
  missing: string;
  action?: 'edit' | 'staff';
}

const has = (v: unknown) => typeof v === 'string' ? v.trim().length > 0 : v != null;

export function venueChecks(p: Record<string, any>, staff: StaffLike[] | null): Check[] {
  const branches = (p.locations ?? []) as Array<{ status?: string }>;
  const tests = (p.capabilities ?? []) as Array<{ paused?: boolean }>;
  const checks: Check[] = [
    {
      key: 'status', label: `Empanelled with ${BRAND.name}`, done: p.status === 'ACTIVE', owner: 'platform',
      missing: p.status === 'ONBOARDING' ? `${BRAND.name} is still reviewing your registration.`
        : `Your venue is ${String(p.status ?? 'not active').toLowerCase()}; no bookings are offered.`,
    },
    {
      key: 'address', label: 'Address', done: has(p.formattedAddress) || (has(p.addressLine1) && has(p.pincode)),
      owner: 'venue', action: 'edit', missing: 'Add the full address with PIN code so employees can find you.',
    },
    {
      key: 'contact', label: 'Contact phone and email', done: has(p.contactPhone) && has(p.contactEmail),
      owner: 'venue', action: 'edit', missing: 'Add a phone number and an email the front desk answers.',
    },
    {
      key: 'emergency', label: 'Emergency phone', done: has(p.emergencyPhone),
      owner: 'venue', action: 'edit', missing: 'Add the number a critical result is phoned to.',
    },
    {
      key: 'hours', label: 'Opening days and hours', done: has(p.operatingDays) && has(p.operatingHours),
      owner: 'venue', action: 'edit', missing: 'Say which days and hours you see employees.',
    },
    {
      key: 'capacity', label: 'Daily capacity', done: Number(p.dailyCapacity) > 0,
      owner: 'venue', action: 'edit', missing: 'Set how many employees you can see in a day. Booking checks it.',
    },
    {
      key: 'branch', label: 'A branch open to bookings',
      done: branches.length === 0 || branches.some((b) => (b.status ?? 'ACTIVE') === 'ACTIVE'),
      owner: 'platform', missing: 'Every branch is closed to bookings. Ask support to reopen one.',
    },
    {
      key: 'tests', label: 'Tests you perform', done: tests.some((t) => !t.paused),
      owner: 'platform',
      missing: tests.length ? 'Every test on your list is paused.' : `${BRAND.name} has not added your tests yet.`,
    },
  ];
  if (staff) {
    const active = staff.filter((u) => u.status === 'ACTIVE');
    checks.push({
      key: 'staff', label: 'Users who can sign in', done: active.length > 1,
      owner: 'venue', action: 'staff',
      missing: 'Add a front desk coordinator and the technicians who take readings.',
    });
    const gap = signoffGap(staff);
    const labWork = gap.kind === 'SIGNER_ENTERS' || (gap.kind === 'NO_SIGNER' && gap.enterers > 0);
    if (labWork) {
      checks.push({
        key: 'signer', label: 'A pathologist to release results', done: false, owner: 'venue', action: 'staff',
        missing: gap.kind === 'NO_SIGNER'
          ? 'Your technicians can enter results, but nobody can validate and release them.'
          : `${gap.signer} is the only pathologist and also enters results; a second one is needed to release those.`,
      });
    }
  }
  return checks;
}

export function VenueReadiness({ partner, staff, canEdit, canAddStaff, onEdit }: {
  partner: Record<string, any>;
  staff: StaffLike[] | null;
  canEdit: boolean;
  canAddStaff: boolean;
  onEdit: () => void;
}) {
  const checks = venueChecks(partner, staff);
  const missing = checks.filter((c) => !c.done);
  const done = checks.length - missing.length;
  const ready = missing.length === 0;
  const pct = Math.round((done / checks.length) * 100);
  const gaps = signingGaps(staff);

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-h4 text-neutral-900">
            {ready ? 'Ready to receive appointments' : `${missing.length} thing${missing.length === 1 ? '' : 's'} to finish before bookings run smoothly`}
          </h2>
          <p className="mt-0.5 text-small text-neutral-600">{done} of {checks.length} set up</p>
        </div>
        <StatusBadge tone={ready ? 'covered' : 'dueSoon'}>{ready ? 'Ready' : 'Setup incomplete'}</StatusBadge>
      </div>
      <div className="mt-3 h-2 rounded-full bg-neutral-100" aria-hidden>
        <div className={cn('h-2 rounded-full', ready ? 'bg-covered-600' : 'bg-primary-500')} style={{ width: `${pct}%` }} />
      </div>

      {missing.length > 0 && (
        <ul className="mt-4 divide-y divide-neutral-100 rounded-btn border border-neutral-200">
          {missing.map((c) => (
            <li key={c.key} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5">
              <CircleDashed aria-hidden className="h-4 w-4 shrink-0 text-dueSoon-600" />
              <div className="min-w-0 flex-[1_1_14rem]">
                <div className="text-small font-medium text-neutral-900">{c.label}</div>
                <div className="text-caption text-neutral-600">{c.missing}</div>
              </div>
              {c.owner === 'platform' ? (
                <span className="text-caption text-neutral-500">Held by {BRAND.name} · ask support</span>
              ) : c.action === 'edit' && canEdit ? (
                <Button size="sm" variant="secondary" onClick={onEdit}>Edit details</Button>
              ) : c.action === 'staff' && canAddStaff ? (
                <Link to="/partner/staff" className="inline-flex h-8 items-center rounded-btn border border-neutral-300 px-3 text-small font-medium text-neutral-800 hover:bg-neutral-50 max-md:h-10">
                  Go to Users
                </Link>
              ) : (
                <span className="text-caption text-neutral-500">Ask your venue admin</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Recommended, not counted toward "Ready": reports sign without these. */}
      {gaps.length > 0 && (
        <div className="mt-4 rounded-btn border border-neutral-200">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-100 px-3 py-2">
            <h3 className="text-small font-semibold text-neutral-900">
              Signing profiles to complete <span className="font-normal text-neutral-500">· recommended</span>
            </h3>
            <span className="text-caption text-neutral-500">Each person adds their own, under My signature</span>
          </div>
          <ul className="divide-y divide-neutral-100">
            {gaps.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <CircleDashed aria-hidden className="h-4 w-4 shrink-0 text-neutral-400" />
                <span className="min-w-0 flex-[1_1_12rem] text-small text-neutral-900">
                  {g.name} <span className="text-caption text-neutral-500">· {g.role}</span>
                </span>
                <span className="text-caption text-neutral-600">No {g.missing.join(' or ')} on file</span>
              </li>
            ))}
          </ul>
          <p className="border-t border-neutral-100 px-3 py-2 text-caption text-neutral-500">
            Reports still sign without these: the box reads “Signed electronically” and no registration is printed.
            Nobody else can add a person’s signature for them.
          </p>
        </div>
      )}

      {done > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-small font-medium text-primary-600 hover:text-primary-700 max-md:py-2">
            {ready ? 'What was checked' : `Show the ${done} done`}
          </summary>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {checks.filter((c) => c.done).map((c) => (
              <li key={c.key} className="flex items-center gap-2 text-small text-neutral-700">
                <CircleCheck aria-hidden className="h-4 w-4 shrink-0 text-covered-600" />{c.label}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
